import { isValidJson } from '@/shared/lib/utils/is-valid-json';
import { clipTextPreview } from '@/shared/lib/utils/clip-text-preview';
import dayjs from 'dayjs';

const jsonWidgetNames = new Set(['json-widget']);

const SECRET_DISPLAY_VALUE = '••••••••';

export const ATTRIBUTE_VALUE_PREVIEW_MAX_CHARS = 500;

export const clipAttributePreview = (
  text: string,
  maxChars = ATTRIBUTE_VALUE_PREVIEW_MAX_CHARS,
) => clipTextPreview(text, maxChars);

export const isOversizedAttributeValue = (
  value: unknown,
  maxChars = ATTRIBUTE_VALUE_PREVIEW_MAX_CHARS,
) => {
  if (typeof value === 'string') {
    return value.length > maxChars;
  }

  let used = 0;
  const visit = (current: unknown): boolean => {
    if (used > maxChars) {
      return true;
    }
    if (current === null || current === undefined) {
      used += 4;
      return used > maxChars;
    }

    const type = typeof current;
    if (type === 'string') {
      used += (current as string).length;
      return used > maxChars;
    }
    if (type === 'number' || type === 'boolean') {
      used += String(current).length;
      return used > maxChars;
    }
    if (Array.isArray(current)) {
      if (current.length > maxChars) {
        return true;
      }
      used += 2;
      for (const item of current) {
        if (visit(item)) {
          return true;
        }
        used += 1;
      }
      return used > maxChars;
    }
    if (type === 'object') {
      const record = current as Record<string, unknown>;
      used += 2;
      let keys = 0;
      for (const key in record) {
        if (!Object.hasOwn(record, key)) {
          continue;
        }
        keys += 1;
        if (keys > maxChars) {
          return true;
        }
        used += key.length;
        if (visit(record[key])) {
          return true;
        }
      }
      return used > maxChars;
    }

    return false;
  };

  return visit(value);
};

const stringifyJsonPreview = (value: unknown, maxChars: number) => {
  let out = '';
  let truncated = false;

  const append = (chunk: string) => {
    if (truncated) {
      return false;
    }
    const room = maxChars - out.length;
    if (room <= 0) {
      truncated = true;
      return false;
    }
    if (chunk.length <= room) {
      out += chunk;
      return true;
    }
    out += chunk.slice(0, room);
    truncated = true;
    return false;
  };

  const walk = (current: unknown): boolean => {
    if (current === null) {
      return append('null');
    }
    if (current === undefined) {
      return append('null');
    }

    const type = typeof current;
    if (type === 'string') {
      const text = current as string;
      if (text.length > maxChars) {
        const json = JSON.stringify(text.slice(0, maxChars));
        return append(`${json.slice(0, -1)}…"`);
      }
      return append(JSON.stringify(text));
    }
    if (type === 'number') {
      return Number.isFinite(current)
        ? append(String(current))
        : append('null');
    }
    if (type === 'boolean') {
      return append(current ? 'true' : 'false');
    }
    if (type !== 'object') {
      return append('null');
    }

    if (Array.isArray(current)) {
      if (!append('[')) {
        return false;
      }
      for (let i = 0; i < current.length; i++) {
        if (i > 0 && !append(',')) {
          return false;
        }
        const item = current[i];
        if (typeof item === 'undefined' || typeof item === 'function') {
          if (!append('null')) {
            return false;
          }
        } else if (!walk(item)) {
          return false;
        }
      }
      return append(']');
    }

    if (!append('{')) {
      return false;
    }
    const record = current as Record<string, unknown>;
    let first = true;
    for (const key in record) {
      if (!Object.hasOwn(record, key)) {
        continue;
      }
      const child = record[key];
      if (typeof child === 'undefined' || typeof child === 'function') {
        continue;
      }
      if (!first && !append(',')) {
        return false;
      }
      first = false;
      if (!append(`${JSON.stringify(key)}:`)) {
        return false;
      }
      if (!walk(child)) {
        return false;
      }
    }
    return append('}');
  };

  walk(value);
  return truncated ? `${out}…` : out;
};

export const previewJson = (
  value: unknown,
  maxChars = ATTRIBUTE_VALUE_PREVIEW_MAX_CHARS,
) => {
  if (typeof value === 'string') {
    const overflow = value.length > maxChars;
    const json = JSON.stringify(overflow ? value.slice(0, maxChars) : value);
    return overflow ? `${json.slice(0, -1)}…"` : json;
  }

  try {
    return stringifyJsonPreview(value, maxChars);
  } catch {
    return clipAttributePreview(String(value), maxChars);
  }
};

export const formatValue = (
  schema,
  uiSchema,
  value,
  maxChars = ATTRIBUTE_VALUE_PREVIEW_MAX_CHARS,
) => {
  if (schema?.format === 'password') {
    return SECRET_DISPLAY_VALUE;
  }
  if (value === null) return 'null';
  if (schema?.oneOf) {
    return schema.oneOf.find((x) => x.const === value)?.title;
  }
  if (schema.format === 'date-time') {
    ///mask value with *
    return dayjs(value).format('DD.MM.YYYY HH:mm:ss');
  }
  if (schema.type === 'boolean') {
    ///mask value with *
    return value?.toString();
  }
  if (jsonWidgetNames.has(uiSchema?.['ui:widget'])) {
    if (typeof value === 'string') {
      return clipAttributePreview(value, maxChars);
    }

    return isValidJson(value) ? value : 'Invalid JSON';
  }
  return typeof value === 'string' || typeof value === 'object'
    ? previewJson(value, maxChars)
    : value;
};